import { ProjectAnalysisTab, ProjectAnalysisTabs } from "@/types/projectAnalysisTab";
import { FeatureManagementType, ModelScopeSettingsItemRespone, Project, ScenarioData } from "@api/apiClient";
import { useAppSettings } from "@api/appSettings";
import { useCadModelCategories } from "@api/cadModelCategories";
import { useComponents, useElements, useModel, useModelScopes } from "@api/models";
import { useProject } from "@api/projects";
import { useScenarios } from "@api/scenarios";
import ProjectStepper from "@components/ProjectStepper/ProjectStepper";
import { SplitPane, Splitter } from "@components/Splitter/Splitter";
import useLmViewers from "@features/analysis/hooks/useLmViewers";
import useProjectAnalysisTabs from "@features/analysis/hooks/useProjectAnalysisTabs";
import useAppStore from "@hooks/useAppStore";
import useScopeStore from "@hooks/useScopeStore";
import useUserPermissionStore from "@hooks/useUserPermissionStore";
import i18n from "@i18n/i18n";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import { useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";
import waitForElm from "@utils/waitForElem";
import { observer } from "mobx-react";
import { SyntheticEvent, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdditionTab from "../AdditionTab/AdditionTab";
import MuiAssignTab from "../MuiAssignTab/MuiAssignTab";
import MuiModelTab from "../MuiModelTab/MuiModelTab";
import ResultTab from "../ResultsTab/ResultsTab";
import ScenariosTab from "../ScenariosTab/ScenariosTab";
import { useCo2MaterialTypes } from "@api/co2MaterialTypes";

function a11yProps(index: any) {
  return {
    id: `tabbar-tab-${index}`,
    "aria-controls": `tabbar-tabpanel-${index}`,
  };
}

const ProjectAnalysisTabBar = observer(() => {
  console.log("ProjectAnalysisTabBar render");

  const theme = useTheme();
  const appStore = useAppStore();
  const { id: projectId } = useParams();
  const [activeTab, setActiveTab] = useState(0);
  const [collapse, setCollapse] = useState(false);
  const [enableViewer1, setEnableViewer1] = useState(true);
  const [enableViewer2, setEnableViewer2] = useState(false);
  const [showViewer2, setShowViewer2] = useState(false);
  const [defaultTab] = useState(+window.location.hash?.replace("#", "") || 0);
  const navigate = useNavigate();
  const [defaultSizes] = useState(appStore.projectTabBarSizes);
  const collapseSizes = [0, 100];
  const upStore = useUserPermissionStore();
  const appSettings = useAppSettings();
  useLmViewers({
    activeTab,
    enableViewer1,
    enableViewer2,
    showViewer2,
  });
  const project = useProject(projectId!);
  useElements(appStore.model?.modelId, appStore.scenario?.scenarioId, { enabled: false });
  useComponents(appStore.model?.modelId, appStore.scenario?.scenarioId, { enabled: false });
  const scopeStore = useScopeStore();
  const scenarios = useScenarios(projectId!);
  const model = useModel(appStore.model?.modelId!, { enabled: !!appStore.model?.modelId });
  useCadModelCategories({ enabled: false });
  const modelScopes = useModelScopes(appStore.model?.modelId, { enabled: false });
  const projectAnalysisTabs = useProjectAnalysisTabs();
  //const co2MaterialTypes = useCo2MaterialTypes(undefined, false, { enabled: false });

  useEffect(() => {
    // TODO: test this with a random guid in the  url
    appStore.setProject(project.data?.project as Project);
    upStore.setResourcePermissions(project.data?.permissions);
  }, [project.data]);

  useEffect(() => {
    //console.log("scenarios: ", data);
    const scenario = scenarios.data?.find((s) => s.scenarioId === appStore.scenario?.scenarioId) || null;
    appStore.setScenario(scenario);
  }, [scenarios.data]);

  useEffect(() => {
    const go = async () => {
      if (model.data && model.data.apsItemName && model.data.apsVersion) {
        if (appStore.model?.modelId !== model.data?.modelId) {
          // TODO: this belongs in appStore?
          // We should have events that indicates new project, model, scenario selected
          appStore.setAssignNodes(null);

          await Promise.all([
            appStore.queryClient.resetQueries({ queryKey: ["scenarios"], exact: false }),
            appStore.queryClient.resetQueries({ queryKey: ["model"], exact: false }),
            appStore.queryClient.resetQueries({ queryKey: ["categories"], exact: false }),
            appStore.queryClient.resetQueries({ queryKey: ["elements"], exact: false }),
            appStore.queryClient.resetQueries({ queryKey: ["components"], exact: false }),
            appStore.queryClient.resetQueries({ queryKey: ["materials"], exact: false })
          ]);
        }
        appStore.setModel(model.data);
      }
    };

    go();
  }, [model.data]);

  useEffect(() => {
    if (modelScopes.data) {
      scopeStore.setModelScope(appStore.model?.modelId!, modelScopes.data);
    }
  }, [modelScopes.data]);

  useEffect(() => {
    scopeStore.setScopes(appSettings.data?.modelScopeSetting?.modelScopeSettingsItems?.filter((item: ModelScopeSettingsItemRespone) => !item.isDeleted) || []);
    // Make all scopes selected by default
    scopeStore.setSelectedScopeIds(scopeStore.scopes?.map((s) => s.id || 0));
  }, [appSettings.data]);

  const handleTabChange = async (_event: SyntheticEvent, newValue: any) => {
    window.location.hash = newValue;
    setActiveTab(newValue);

    // TODO: this should really be done based on selectionManager.selectionMode === 'DualScenarios'
    if (projectAnalysisTabs.tabs.at(newValue) === ProjectAnalysisTabs.Results) {
      setEnableViewer2(true);
    } else {
      setShowViewer2(false);
      setEnableViewer2(false);
    }

    // TODO: these selections should be part of the SelectionManager. They are hold-over from the previous selection behavior of one model at a time.
    // Reset scenario and model selection when switching tabs
    appStore.setModel(null);
    appStore.setScenario(null);
  };

  const handleResultsTabSelectChange = async (selectedScenarioDatas: ScenarioData[]) => {
    console.log("handleResultsTabSelectChange", selectedScenarioDatas);

    if (projectAnalysisTabs.tabs.at(activeTab) === ProjectAnalysisTabs.Results && selectedScenarioDatas.length > 1) {
      setShowViewer2(true);
    }

    // Handle viewer1 and viewer2 swapping
    if (selectedScenarioDatas.length === 1) {
      const scenarioId = (document.querySelector("#lmViewer1")?.firstChild as HTMLElement)?.getAttribute("data-scenario-id");
      if (scenarioId !== null && scenarioId !== selectedScenarioDatas.at(0)?.scenarioId) {
        const element1 = document.querySelector("#lmViewer1") as HTMLElement;
        if (element1) {
          const viewer1 = document.querySelector("#lmViewer1")?.firstChild as HTMLElement;
          const viewer2 = document.querySelector("#lmViewer2")?.firstChild as HTMLElement;
          if (viewer2) {
            const container = viewer1?.id === "lmViewer1-viewer" ? (appStore.lmViewer2?.viewer?.container as HTMLElement) : (appStore.lmViewer1?.viewer?.container as HTMLElement);
            if (container) {
              element1?.removeChild(viewer1);
              element1?.appendChild(container);
            }
          } else if (viewer1?.id === "lmViewer2-viewer") {
            const container = appStore.lmViewer1?.viewer?.container as HTMLElement;
            if (container) {
              element1?.removeChild(viewer1);
              element1?.appendChild(container);
              appStore.lmViewer1?.viewer.resize();
            }
          }
        }
      }
    } else if (selectedScenarioDatas.length === 2) {
      const viewer1 = document.querySelector("#lmViewer1")?.firstChild as HTMLElement;
      const scenarioId = viewer1?.getAttribute("data-scenario-id");
      if (scenarioId !== selectedScenarioDatas.at(0)?.scenarioId) {
        try {
          await waitForElm("#lmViewer1");
          await waitForElm("#lmViewer1-viewer", 2000);
          await waitForElm("#lmViewer2");
          await waitForElm("#lmViewer2-viewer", 2000);
        } catch {}

        const newViewer1 = viewer1.id === "lmViewer1-viewer" ? (appStore.lmViewer2?.viewer?.container as HTMLElement) : (appStore.lmViewer1?.viewer?.container as HTMLElement);
        const newViewer2 = viewer1.id === "lmViewer1-viewer" ? (appStore.lmViewer1?.viewer?.container as HTMLElement) : (appStore.lmViewer2?.viewer?.container as HTMLElement);

        document.querySelector("#lmViewer1")?.appendChild(newViewer1);
        document.querySelector("#lmViewer2")?.appendChild(newViewer2);
      } else {
        try {
          await waitForElm("#lmViewer2");
          await waitForElm("#lmViewer1-viewer", 2000);
          await waitForElm("#lmViewer2-viewer", 2000);
        } catch {}

        const newViewer2 = viewer1.id === "lmViewer1-viewer" ? (appStore.lmViewer2?.viewer?.container as HTMLElement) : (appStore.lmViewer1?.viewer?.container as HTMLElement);
        document.querySelector("#lmViewer2")?.appendChild(newViewer2);
      }
    }

    (document.querySelector("#lmViewer1")?.firstChild as HTMLElement)?.setAttribute("data-scenario-id", selectedScenarioDatas.at(0)?.scenarioId!);
    (document.querySelector("#lmViewer2")?.firstChild as HTMLElement)?.setAttribute("data-scenario-id", selectedScenarioDatas.at(1)?.scenarioId!);

    if (projectAnalysisTabs.tabs.at(activeTab) === ProjectAnalysisTabs.Results && selectedScenarioDatas.length <= 1) {
      setShowViewer2(false); // TODO:
    }
  };

  useEffect(() => {
    navigate(window.location.pathname);
    window.location.hash = defaultTab.toString();
    setActiveTab(defaultTab);
    setEnableViewer2(projectAnalysisTabs.tabs.at(defaultTab) === ProjectAnalysisTabs.Results);
  }, [defaultTab]);

  useEffect(() => {
    if (window.location.hash !== `#${activeTab}`) {
      setActiveTab(+window.location.hash?.replace("#", "") || 0);
    }
  }, [window.location.hash]);

  useEffect(() => {
    return () => {
      console.log("ProjectAnalysisTabBar unmount");
    };
  }, []);

  const onDragEnd = (event: any) => {
    console.log(event);
    appStore.setProjectTabBarSizes(event);
    appStore.lmViewer1?.viewer.resize();
    appStore.lmViewer2?.viewer.resize();
  };

  // Syncronize the views with selectedScenarios
  // Unload any urns that are not in selectedScenarios[0] and appStore.
  return (
    <>
      <ProjectStepper />
      <Tabs
        value={activeTab}
        onChange={(e, v) => handleTabChange(e, v)}
        sx={{
          // @ts-ignore Property 'custom' does not exist on type 'Theme'
          "& .MuiButtonBase-root": { backgroundColor: theme.custom.tabs.background },
          "& .MuiButtonBase-root.Mui-selected": {
            backgroundColor: theme.palette.action.selected,
          },
          "& .MuiTabs-indicator": {
            top: 0,
            bottom: "unset",
            backgroundColor: theme.palette.grey[600],
          },
        }}
      >
        <Tab key={ProjectAnalysisTab.Models} label={i18n.t("project-tab-bar.tabs.models")} {...a11yProps(ProjectAnalysisTabs.Models)} />
        <Tab key={ProjectAnalysisTab.Scenarios} label={i18n.t("project-tab-bar.tabs.scenarios")} {...a11yProps(ProjectAnalysisTabs.Scenarios)} />
        <Tab key={ProjectAnalysisTab.Assign} label={i18n.t("project-tab-bar.tabs.assign")} {...a11yProps(ProjectAnalysisTabs.Assign)} />
        <Tab key={ProjectAnalysisTab.Results} label={i18n.t("project-tab-bar.tabs.results")} {...a11yProps(ProjectAnalysisTabs.Results)} />
      </Tabs>

      <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1, width: "99.7%" }}>
        <Splitter onDragEnd={(e: any) => onDragEnd(e)} minSize={0} sizes={collapse ? collapseSizes : defaultSizes} style={{ display: "flex", flexDirection: "row", flexGrow: 1 }}>
          <SplitPane style={{ minWidth: 0 }}>
            <Stack spacing={1} sx={{ width: "100%", height: "100%" }}>
              <Box id="lmViewer1" style={{ position: "relative", width: "100%", height: showViewer2 ? "50%" : "100%" }}></Box>
              {showViewer2 && <Box id="lmViewer2" style={{ position: "relative", width: "100%", height: "50%" }}></Box>}
            </Stack>
          </SplitPane>
          <SplitPane style={{ position: "relative", minWidth: 300 }}>
            {!collapse && (
              <Tooltip title={i18n.t("project-tab-bar.close-model-viewer-button.tooltip")}>
                <IconButton sx={{ position: "absolute", bottom: "0", zIndex: 2 }} onClick={() => setCollapse(true)}>
                  <KeyboardDoubleArrowLeftIcon />
                </IconButton>
              </Tooltip>
            )}
            {collapse && (
              <Tooltip title={i18n.t("project-tab-bar.open-model-viewer-button.tooltip")}>
                <span>
                  <IconButton sx={{ position: "absolute", bottom: "0", zIndex: 2 }} onClick={() => setCollapse(false)}>
                    <KeyboardDoubleArrowRightIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <TabPanel key={ProjectAnalysisTab.Models} value={activeTab} index={projectAnalysisTabs.getTabIndex(ProjectAnalysisTab.Models)} sx={{ width: "100%" }}>
              <MuiModelTab />
            </TabPanel>
            <TabPanel key={ProjectAnalysisTab.Scenarios} value={activeTab} index={projectAnalysisTabs.getTabIndex(ProjectAnalysisTab.Scenarios)} sx={{ width: "100%" }}>
              <ScenariosTab />
            </TabPanel>
            <TabPanel key={ProjectAnalysisTab.Assign} value={activeTab} index={projectAnalysisTabs.getTabIndex(ProjectAnalysisTab.Assign)} sx={{ width: "100%" }}>
              <MuiAssignTab />
            </TabPanel>
            <TabPanel key={ProjectAnalysisTab.Results} value={activeTab} index={projectAnalysisTabs.getTabIndex(ProjectAnalysisTab.Results)} sx={{ width: "100%" }}>
              <ResultTab onSelectChange={(v: ScenarioData[]) => handleResultsTabSelectChange(v)} />
            </TabPanel>
          </SplitPane>
        </Splitter>
      </Box>
    </>
  );
});

const TabPanel = observer((props: any) => {
  const { children, value, index, ...other } = props;

  return (
    <div key={`tab-${index}`} className="tabpanel" role="tabpanel" hidden={value !== index} id={`tabbar-tabpanel-${index}`} aria-labelledby={`tabbar-tab-${index}`} {...other}>
      {value === index && children}
    </div>
  );
});

export default ProjectAnalysisTabBar;
